import { buildPermission } from "portal/utils/auth";
import { convert } from "portal/utils/units/units";
import { GlobalHotKeys } from "react-hotkeys";
import { Header } from "portal/components/header/Header";
import { Page } from "portal/components/Page";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { range } from "portal/utils/arrays";
import { ResponsiveSubnav } from "portal/components/ResponsiveSubnav";
import { RobotSelector } from "portal/components/robots/RobotSelector";
import { RobotView } from "portal/views/robots/AnyRobotPage";
import { Skeleton } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import { useAtkRobotPageNav } from "portal/components/atk/AtkRobotSubnav";
import { useJigsawMetrics } from "../../utils/hooks/useJigsawMetrics";
import {
  useLocation,
  useNavigate,
  useParams,
  useRoutes,
} from "react-router-dom";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import { withAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import React, { FunctionComponent, useState } from "react";

interface AtkRobotPageProps {
  offerViewSwitchButton?: RobotView;
}

const _AtkRobotPage: FunctionComponent<AtkRobotPageProps> = ({
  offerViewSwitchButton,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  const useJigsaw = useJigsawMetrics();

  // ### ROBOT
  const { serial } = useParams();
  const { data: summary, isSuccess } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken,
      {
        pollingInterval: convert(1).from("min").to("ms"),
      }
    )
  );

  const [subtitle, setSubtitle] = useState<string>("");

  let name: string = "";
  if (isSuccess) {
    name = valueOrLoading(summary.robot?.customerSerial, t);
  }

  const nav = useAtkRobotPageNav(offerViewSwitchButton, summary);
  const element = useRoutes(nav.routes);

  return (
    <>
      <GlobalHotKeys
        allowChanges
        keyMap={nav.keymap}
        handlers={nav.globalHotKeysHandlers}
      />
      <Header
        hideTitle={true}
        title={name}
        subtitle={subtitle}
        parentLink={Path.FLEET}
      >
        <RobotSelector
          serial={serial}
          customerSerial={summary?.robot?.customerSerial}
          onChange={({ robot }) => {
            if (!serial || !robot) {
              return;
            }
            navigate(pathname.replace(serial, robot.serial));
          }}
          typography="h1"
          typographyClassName="text-lg"
        />
        {summary ? (
          <ResponsiveSubnav
            onChange={(index, name) => setSubtitle(name)}
            pages={nav.subnavPages}
          />
        ) : (
          <>
            {range(3).map((index) => (
              <Skeleton
                variant="rectangular"
                className="w-16 h-full"
                key={index}
              />
            ))}
          </>
        )}
      </Header>
      <Page>{element}</Page>
    </>
  );
};

export const AtkRobotPage = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.robots,
        PermissionDomain.all
      ),
    ],
  },
  _AtkRobotPage,
  <UnauthorizedPage />
);
